import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Upload, Loader2, Camera, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

interface AvatarPickerProps {
  open: boolean;
  onClose: () => void;
  currentAvatarUrl?: string | null;
  onAvatarChange: (url: string) => void;
}

const PRESET_AVATARS = [
  { id: "popcorn", emoji: "🍿", label: "Popcorn" },
  { id: "clapper", emoji: "🎬", label: "Clapper" },
  { id: "film", emoji: "🎞️", label: "Film" },
  { id: "ticket", emoji: "🎟️", label: "Ticket" },
  { id: "star", emoji: "⭐", label: "Star" },
  { id: "tv", emoji: "📺", label: "TV" },
  { id: "cinema", emoji: "🎥", label: "Camera" },
  { id: "glasses", emoji: "🥽", label: "3D Glasses" },
  { id: "couch", emoji: "🛋️", label: "Couch" },
  { id: "spotlight", emoji: "🔦", label: "Spotlight" },
  { id: "mask", emoji: "🎭", label: "Drama" },
  { id: "rocket", emoji: "🚀", label: "Sci-Fi" },
  { id: "ghost", emoji: "👻", label: "Horror" },
  { id: "sword", emoji: "⚔️", label: "Action" },
  { id: "heart", emoji: "💖", label: "Romance" },
  { id: "alien", emoji: "👽", label: "Alien" },
  { id: "robot", emoji: "🤖", label: "Robot" },
  { id: "unicorn", emoji: "🦄", label: "Fantasy" },
  { id: "detective", emoji: "🕵️", label: "Mystery" },
  { id: "crystal", emoji: "🔮", label: "Magic" },
];

const AvatarPicker = ({ open, onClose, currentAvatarUrl, onAvatarChange }: AvatarPickerProps) => {
  const { t } = useTranslation();
  const { user, refreshProfile } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);

  const handlePresetSelect = async (preset: typeof PRESET_AVATARS[0]) => {
    if (!user) return;
    setSelectedPreset(preset.id);
    
    const avatarUrl = `emoji:${preset.emoji}`;
    const { error } = await (supabase.from("profiles") as any)
      .update({ avatar_url: avatarUrl })
      .eq("id", user.id);

    if (error) {
      toast.error(t("avatar.failedUpdate"));
      setSelectedPreset(null);
      return;
    }

    await refreshProfile();
    onAvatarChange(avatarUrl);
    toast.success(t("avatar.updated"));
    onClose();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("avatar.selectImage"));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t("avatar.imageTooLarge"));
      return;
    }

    setUploading(true);

    try {
      const reader = new FileReader();
      const base64 = await new Promise<string>((resolve, reject) => {
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      setUploadPreview(base64);

      const { data: modResult, error: modError } = await supabase.functions.invoke("moderate-avatar", {
        body: { image_base64: base64 },
      });

      if (modError) throw modError;

      if (!modResult?.safe) {
        toast.error(modResult?.reason || t("avatar.guidelinesFail"));
        setUploadPreview(null);
        setUploading(false);
        return;
      }

      const fileExt = file.name.split(".").pop() || "jpg";
      const filePath = `${user.id}/avatar.${fileExt}`;

      await supabase.storage.from("avatars").remove([filePath]);

      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(filePath, file, { upsert: true, contentType: file.type });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(filePath);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { error: profileError } = await (supabase.from("profiles") as any)
        .update({ avatar_url: publicUrl })
        .eq("id", user.id);

      if (profileError) throw profileError;

      await refreshProfile();
      onAvatarChange(publicUrl);
      toast.success(t("avatar.profileUpdated"));
      onClose();
    } catch (err: any) {
      console.error("Upload error:", err);
      toast.error(err.message || t("avatar.failedUpdate"));
      setUploadPreview(null);
    } finally {
      setUploading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25 }}
            className="w-full max-w-sm bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-card overflow-hidden max-h-[85dvh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 pb-2 flex items-center justify-between border-b border-border">
              <h2 className="font-display text-lg font-bold text-foreground">{t("avatar.title")}</h2>
              <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-secondary transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">{t("avatar.uploadPhoto")}</p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-secondary border border-border hover:border-primary/30 transition-colors disabled:opacity-50"
                >
                  {uploading ? (
                    <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    </div>
                  ) : uploadPreview ? (
                    <img src={uploadPreview} className="w-10 h-10 rounded-full object-cover" alt="Preview" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-primary" />
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-foreground">
                      {uploading ? t("avatar.checkingImage") : t("avatar.uploadAPhoto")}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {uploading ? t("avatar.aiReviewing") : t("avatar.maxSize")}
                    </p>
                  </div>
                  {!uploading && <Upload className="w-4 h-4 text-muted-foreground" />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 font-medium">{t("avatar.movieIcons")}</p>
                <div className="grid grid-cols-5 gap-2">
                  {PRESET_AVATARS.map((preset) => {
                    const isSelected = currentAvatarUrl === `emoji:${preset.emoji}`;
                    return (
                      <button
                        key={preset.id}
                        onClick={() => handlePresetSelect(preset)}
                        className={`relative aspect-square rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all active:scale-90 ${
                          isSelected
                            ? "bg-primary/20 border-2 border-primary"
                            : "bg-secondary border border-border hover:border-primary/30"
                        }`}
                      >
                        <span className="text-2xl">{preset.emoji}</span>
                        <span className="text-[8px] text-muted-foreground leading-none">{preset.label}</span>
                        {isSelected && (
                          <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary flex items-center justify-center">
                            <Check className="w-2.5 h-2.5 text-primary-foreground" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AvatarPicker;
